import type { CSSProperties } from "react";
import plume from "./ornements/plume.svg";
import { POLICE_TITRE } from "./styles.js";

/**
 * ============================================================================
 *  AnnonceScene.tsx — La composition du titre, propre à ce projet
 * ----------------------------------------------------------------------------
 *  Le moteur décide QUAND l'annonce paraît, résout le nom dans la langue
 *  courante et assombrit la scène. Tout ce qui suit — la plume, la disposition,
 *  le filet — n'appartient qu'à cette visite, et c'est bien pour cela que ça
 *  vit ici et non dans `@latelierdanslesnuages/player`.
 *
 *  ── La plume est un MASQUE, pas une image ──────────────────────────────────
 *
 *  Le fichier est un tracé noir. Posé en `<img>`, il resterait noir sur une
 *  photographie sombre, et rien en CSS ne peut recolorer le contenu d'une
 *  image. En masque, c'est un aplat de `currentColor` qui prend sa forme : la
 *  plume suit alors la couleur du texte, d'un seul réglage, et le fichier reste
 *  un asset externe mis en cache pour un an — au lieu de gonfler le bundle de
 *  vingt kilo-octets de tracé à analyser à chaque chargement.
 *
 *  ── Lisibilité sur une photographie ────────────────────────────────────────
 *
 *  En reprenant la composition à notre compte, on reprend aussi le problème que
 *  le contour du moteur résolvait : un blanc sur ciel clair disparaît. La
 *  réponse ici est une ombre portée plutôt qu'un contour — la maquette montre
 *  des lettres franches, et un contour noir de trois pixels épaissirait les
 *  déliés d'une didone jusqu'à les fermer.
 * ==========================================================================*/

/** Rapport largeur / hauteur du tracé, mesuré sur le `viewBox` recadré. */
const RAPPORT_PLUME = 0.303;

/** Hauteur de la plume, en corps du titre. */
const HAUTEUR_PLUME = "2.7em";

const bloc: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.55em",
  // La couleur est posée une fois : le texte l'utilise, et la plume la reprend
  // par `currentColor` à travers son masque.
  color: "#fff",
};

const ornement: CSSProperties = {
  flex: "0 0 auto",
  height: HAUTEUR_PLUME,
  width: `calc(${HAUTEUR_PLUME} * ${RAPPORT_PLUME})`,
  backgroundColor: "currentColor",
  maskImage: `url(${plume})`,
  maskRepeat: "no-repeat",
  maskSize: "contain",
  maskPosition: "center",
  // Safari n'a levé le préfixe qu'en 15.4 ; le coût de le garder est nul.
  WebkitMaskImage: `url(${plume})`,
  WebkitMaskRepeat: "no-repeat",
  WebkitMaskSize: "contain",
  WebkitMaskPosition: "center",
};

const nomStyle: CSSProperties = {
  fontFamily: POLICE_TITRE,
  textTransform: "uppercase",
  fontWeight: 600,
  letterSpacing: "0.06em",
  // Compense la chasse ajoutée après la dernière lettre par l'interlettrage.
  textIndent: "0.06em",
  lineHeight: 1.1,
  paddingBottom: "0.1em",
  // Le filet suit exactement la largeur du nom, sans réglage à maintenir.
  borderBottom: "1px solid currentColor",
  textShadow: "0 2px 14px rgba(0,0,0,0.55)",
  whiteSpace: "nowrap",
};

export interface AnnonceSceneProps {
  nom: string;
}

export function AnnonceScene({ nom }: AnnonceSceneProps) {
  return (
    <div style={bloc}>
      <span aria-hidden style={ornement} />
      <span style={nomStyle}>{nom}</span>
    </div>
  );
}
